/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { Reducer } from 'redux';
import { MultiLevelDataActions } from '../actions/multiLevelDataActions';
import { IState } from '../store/types/StateTypes';
import { MetadataActions } from '../actions/metadataActions';
import datasetTypes from '../enums/datasetTypes';
import { IMultiLevelReducerActionData, IReducerAction } from '../store/types/ReducerTypes';
import { IMultiLevelDataset, IMultiLevelDatasetNode, NodeType } from '../store/types/MultiLevelDatasetTypes';
import DataState from '../enums/DataState';

function sameNode(node1: IMultiLevelDatasetNode, node2: IMultiLevelDatasetNode) {
    let same;

    if (node1.nodeType === NodeType.DETAIL) {
        let i = 0;
        const max = Math.max(node1.values.length, node2.values.length);
        for (same = true; same && i < max; i += 1) {
            same = node2.values[i] === node1.values[i];
        }
    } else {
        same = node1.label === node2.label;
    }

    return same;
}

function mergeMultiLevelData(existing: IMultiLevelDatasetNode, loaded: IMultiLevelDatasetNode): IMultiLevelDatasetNode {
    if (existing.nodeType === loaded.nodeType) {
        if (existing.nodeType !== NodeType.DETAIL) {
            const ex = existing;
            if (existing.children.length) {
                let existingCounter = existing.children.length - 1;
                const loadedCounter = 0;

                while (existingCounter >= 0 && !sameNode(existing.children[existingCounter], loaded.children[loadedCounter])) {
                    existingCounter -= 1;
                }

                if (existingCounter < 0) {
                    throw new Error('First loaded node was not found in existing nodes.');
                } else {
                    const res = existing.children.slice(0, existingCounter);
                    res.push(mergeMultiLevelData(existing.children[existingCounter], loaded.children[0]));
                    ex.children = res.concat(loaded.children.slice(1));
                }
            } else {
                ex.children = loaded.children;
            }
        }
    } else {
        throw new Error('An attempt to merge different kind of nodes');
    }

    return existing;
}

function resetMultiLevelDataset(state: IState) {
    return {
        ...state,
        dataState: DataState.LOADING,
        dataError: null,
        data: {
            ...state.data,
            [datasetTypes.MULTI_LEVEL]: {
                summary: {
                    complete: false,
                    groups: [],
                    details: [],
                    aggregations: [],
                    filters: {
                        expression: state.filterExpression,
                        parameters: state.filterParameters
                    },
                    order: state.orderInformation.levels,
                    limit: state.dataLimit
                },
                data: {
                    index: -1,
                    nodeType: 'root',
                    children: [],
                    values: []
                }
            }
        } as IMultiLevelDataset
    };
}

const multiLevelDataReducer: Reducer<any, IReducerAction<IMultiLevelReducerActionData>> = (state: IState, action: IReducerAction<IMultiLevelReducerActionData>) => {
    let newState = state;

    if (action.type === MetadataActions.METADATA_LOADED) {
        // discard dataset because metadata was updated
        newState = resetMultiLevelDataset(newState);
    }

    if (action.type === MultiLevelDataActions.MULTI_LEVEL_DATA_LOADED) {
        if (action.data) {
            const { dataObj } = action.data;
            const data = state.data[datasetTypes.MULTI_LEVEL].data.children.length
                ? mergeMultiLevelData(state.data[datasetTypes.MULTI_LEVEL].data, dataObj.data) : dataObj.data;

            newState = {
                ...state,
                dataState: DataState.LOADED,
                dataError: null,
                data: {
                    ...state.data,
                    [datasetTypes.MULTI_LEVEL]: {
                        ...dataObj,
                        data
                    }
                }
            };
        }
    }

    if (action.type === MultiLevelDataActions.MULTI_LEVEL_DATA_ERROR) {
        newState = {
            ...state,
            dataState: DataState.ERROR,
            dataError: action.errorDescriptor
        }
    }

    return newState;
};

export { multiLevelDataReducer, resetMultiLevelDataset };
